<template>
    <div class="team-panel">
        <div class="team-toolbar" v-if="is_admin">
            <a-button type="primary" @click="showAdd">{{ $t('team.add') }}</a-button>
        </div>
        <a-table v-if="!is_mobile" :columns="tableColumns" :data-source="list" :pagination="false" :loading="loading" row-key="rowKey">
            <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'role'">
                    {{ record.role === 'admin' ? $t('team.admin') : $t('team.member') }}
                </template>
                <template v-else-if="column.key === 'status'">
                    {{ statusText(record.status) }}
                </template>
                <template v-else-if="column.key === 'action'">
                    <span v-if="is_admin && record.role === 'member'">
                        <template v-if="record.status !== 'joined'">
                            <a href="javascript:;" @click="resend(record)">{{ $t('team.resend') }}</a>
                            <a-divider type="vertical" />
                            <a href="javascript:;" @click="copyLink(record)">{{ $t('team.copy_link') }}</a>
                            <a-divider type="vertical" />
                            <a href="javascript:;" @click="revoke(record)">{{ $t('team.revoke') }}</a>
                        </template>
                        <template v-else>
                            <a href="javascript:;" @click="showReset(record)">{{ $t('team.reset_password') }}</a>
                            <a-divider type="vertical" />
                            <a href="javascript:;" @click="remove(record)">{{ $t('team.remove') }}</a>
                        </template>
                    </span>
                </template>
                <template v-else>
                    {{ record[column.dataIndex] }}
                </template>
            </template>
        </a-table>
        <div v-else>
            <a-card v-for="item in list" :key="item.rowKey" style="margin-top: 8px;">
                <p>{{ $t('team.email') }}: {{ item.email }}</p>
                <p>{{ $t('team.name') }}: {{ item.name }}</p>
                <p>{{ $t('team.role') }}: {{ item.role === 'admin' ? $t('team.admin') : $t('team.member') }}</p>
                <p>{{ $t('team.status') }}: {{ statusText(item.status) }}</p>
                <div v-if="is_admin && item.role === 'member'">
                    <template v-if="item.status !== 'joined'">
                        <a href="javascript:;" @click="resend(item)">{{ $t('team.resend') }}</a>
                        <a-divider type="vertical" />
                        <a href="javascript:;" @click="copyLink(item)">{{ $t('team.copy_link') }}</a>
                        <a-divider type="vertical" />
                        <a href="javascript:;" @click="revoke(item)">{{ $t('team.revoke') }}</a>
                    </template>
                    <template v-else>
                        <a href="javascript:;" @click="showReset(item)">{{ $t('team.reset_password') }}</a>
                        <a-divider type="vertical" />
                        <a href="javascript:;" @click="remove(item)">{{ $t('team.remove') }}</a>
                    </template>
                </div>
            </a-card>
        </div>

        <a-modal :title="$t('team.add')" v-model:open="show_add" @ok="submitInvite" :confirm-loading="saving" :cancelText="$t('common.cancel')" :okText="$t('common.ok')">
            <div>{{ $t('team.email') }}</div>
            <a-input v-model:value="invite_email" :placeholder="$t('team.email')" />
        </a-modal>
        <a-modal :title="$t('team.reset_password')" v-model:open="show_reset" @ok="submitReset" :confirm-loading="saving" :cancelText="$t('common.cancel')" :okText="$t('common.ok')">
            <div>{{ $t('team.new_password') }}</div>
            <a-input v-model:value="new_password" type="password" />
            <div style="margin-top: 12px;">{{ $t('team.confirm_password') }}</div>
            <a-input v-model:value="confirm_password" type="password" />
        </a-modal>
    </div>
</template>

<script>
    import md5 from 'md5';
    import { mapActions } from 'vuex';
    import { Modal, notification } from 'ant-design-vue';

    export default {
        name: 'TeamPanel',
        props: {
            is_admin: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                is_mobile: this.$store.state.app.is_mobile,
                list: [],
                columns: [
                    { title: this.$t('team.email'), dataIndex: 'email', key: 'email' },
                    { title: this.$t('team.name'), dataIndex: 'name', key: 'name' },
                    { title: this.$t('team.role'), dataIndex: 'role', key: 'role' },
                    { title: this.$t('team.status'), dataIndex: 'status', key: 'status' },
                    { title: this.$t('common.action'), key: 'action' },
                ],
                show_add: false,
                show_reset: false,
                invite_email: '',
                reset_id: null,
                new_password: '',
                confirm_password: '',
                saving: false,
                loading: false,
            };
        },
        created() {
            this.loadList();
        },
        computed: {
            tableColumns() {
                if (this.is_admin) {
                    return this.columns;
                }
                return this.columns.filter((column) => column.key !== 'action');
            },
        },
        methods: {
            ...mapActions({
                getTeamList: 'team/getTeamList',
                inviteMember: 'team/inviteMember',
                resendInvite: 'team/resendInvite',
                revokeInvite: 'team/revokeInvite',
                removeMember: 'team/removeMember',
                resetMemberPassword: 'team/resetMemberPassword',
            }),
            statusText(status) {
                if (status === 'pending') {
                    return this.$t('team.pending');
                }
                if (status === 'expired') {
                    return this.$t('team.expired');
                }
                return this.$t('team.joined');
            },
            async loadList() {
                this.loading = true;
                try {
                    const res = await this.getTeamList();
                    const rows = (res && res.data && res.data.list) || [];
                    this.list = rows.map((item, index) => {
                        return Object.assign({ rowKey: (item.role || 'row') + '-' + (item.id || index) }, item);
                    });
                } catch (e) {
                    notification.warning({ message: 'Load team failed' });
                }
                this.loading = false;
            },
            showResult(res, successText) {
                if (res && res.code == 100000) {
                    if (res.data && res.data.email_sent === false) {
                        notification.warning({ message: this.$t('team.email_failed') });
                    } else if (successText) {
                        this.$message.success(successText);
                    }
                    return true;
                }
                notification.warning({ message: (res && res.msg) || 'Request failed' });
                return false;
            },
            showAdd() {
                this.invite_email = '';
                this.show_add = true;
            },
            async submitInvite() {
                const email = (this.invite_email || '').trim();
                if (!email) {
                    notification.warning({ message: this.$t('team.email_required') });
                    return;
                }
                this.saving = true;
                try {
                    const res = await this.inviteMember({ email });
                    if (this.showResult(res, this.$t('team.invited'))) {
                        this.show_add = false;
                        await this.loadList();
                    }
                } catch (e) {
                    notification.warning({ message: 'Request failed' });
                }
                this.saving = false;
            },
            async resend(record) {
                try {
                    const res = await this.resendInvite({ member_id: record.id });
                    if (this.showResult(res, this.$t('team.invited'))) {
                        await this.loadList();
                    }
                } catch (e) {
                    notification.warning({ message: 'Request failed' });
                }
            },
            async copyLink(record) {
                const url = record.invite_url || '';
                const full = url.indexOf('http') === 0 ? url : window.location.origin + url;
                try {
                    await navigator.clipboard.writeText(full);
                    this.$message.success(this.$t('team.copied'));
                } catch (e) {
                    notification.warning({ message: full });
                }
            },
            revoke(record) {
                const that = this;
                Modal.confirm({
                    title: this.$t('team.revoke_confirm'),
                    okText: this.$t('common.ok'),
                    cancelText: this.$t('common.cancel'),
                    onOk() {
                        return that.revokeInvite({ member_id: record.id }).then((res) => {
                            if (that.showResult(res, that.$t('team.revoked'))) {
                                return that.loadList();
                            }
                        });
                    },
                });
            },
            remove(record) {
                const that = this;
                Modal.confirm({
                    title: this.$t('team.remove_confirm'),
                    okText: this.$t('common.ok'),
                    cancelText: this.$t('common.cancel'),
                    onOk() {
                        return that.removeMember({ member_id: record.id }).then((res) => {
                            if (that.showResult(res, that.$t('team.removed'))) {
                                return that.loadList();
                            }
                        });
                    },
                });
            },
            showReset(record) {
                this.reset_id = record.id;
                this.new_password = '';
                this.confirm_password = '';
                this.show_reset = true;
            },
            async submitReset() {
                if (!this.new_password || this.new_password.length < 6 || this.new_password.length > 30) {
                    notification.warning({ message: this.$t('team.password_rule') });
                    return;
                }
                if (this.new_password !== this.confirm_password) {
                    notification.warning({ message: this.$t('team.password_mismatch') });
                    return;
                }
                this.saving = true;
                try {
                    const res = await this.resetMemberPassword({
                        member_id: this.reset_id,
                        pswd: md5(this.new_password),
                    });
                    if (this.showResult(res, this.$t('team.password_updated'))) {
                        this.show_reset = false;
                    }
                } catch (e) {
                    notification.warning({ message: 'Request failed' });
                }
                this.saving = false;
            },
        },
    };
</script>

<style scoped>
.team-toolbar {
    margin-bottom: 12px;
}
</style>
